// Scope: waits for the free-tier API to wake before mounting server-backed demos.

import React, { useEffect, useState } from "react";
import { buildApiUrl } from "../Services/apiConfig";
import "./BackendGate.css";

const RETRY_DELAY_MS = 3_000;
const REQUEST_TIMEOUT_MS = 6_000;

function BackendGate({ children, featureName = "this demo", healthPath = "health" }) {
    const [status, setStatus] = useState("connecting");
    const [startedAt] = useState(() => Date.now());
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [retryToken, setRetryToken] = useState(0);

    useEffect(() => {
        const timer = window.setInterval(() => {
            setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
        }, 1_000);
        return () => window.clearInterval(timer);
    }, [startedAt]);

    useEffect(() => {
        let cancelled = false;
        let retryTimer;

        const checkServer = async () => {
            const controller = new AbortController();
            const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

            try {
                const response = await fetch(buildApiUrl(healthPath), {
                    method: "GET",
                    headers: { Accept: "application/json" },
                    signal: controller.signal,
                });

                if (!response.ok) throw new Error(`Health check returned ${response.status}`);
                if (!cancelled) setStatus("ready");
            } catch {
                if (!cancelled) {
                    setStatus("waking");
                    retryTimer = window.setTimeout(checkServer, RETRY_DELAY_MS);
                }
            } finally {
                window.clearTimeout(timeout);
            }
        };

        setStatus("connecting");
        checkServer();

        return () => {
            cancelled = true;
            window.clearTimeout(retryTimer);
        };
    }, [healthPath, retryToken]);

    if (status === "ready") return children;

    const isTakingLonger = elapsedSeconds >= 8;
    return (
        <section className="backend-gate" aria-live="polite" aria-busy="true">
            <div className="backend-gate__visual" aria-hidden="true">
                <span className="backend-gate__pulse" />
                <span className="backend-gate__server">API</span>
            </div>
            <p className="backend-gate__eyebrow">Free-tier backend</p>
            <h1>{isTakingLonger ? "Waking up the server" : "Connecting to the server"}</h1>
            <p className="backend-gate__description">
                The backend sleeps when inactive to keep this portfolio demo free. A cold start can take
                about 30–60 seconds. {featureName} will open automatically as soon as it is ready.
            </p>
            <div className="backend-gate__progress" aria-hidden="true"><span /></div>
            <div className="backend-gate__meta">
                <span>{status === "waking" ? "Server is starting…" : "Checking availability…"}</span>
                <span>{elapsedSeconds}s</span>
            </div>
            {elapsedSeconds >= 45 && (
                <button type="button" onClick={() => setRetryToken((value) => value + 1)}>
                    Check again now
                </button>
            )}
        </section>
    );
}

export default BackendGate;
